import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Tag,
  Table,
  Select,
  DatePicker,
  Input,
  Spin,
  message,
  Button,
} from "antd";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { adminService } from "../../_services/admin.service";

const { Option } = Select;

const CATEGORY_FILTER_OPTIONS = [
  { value: "all", label: "सर्व" },
  { value: "Day", label: "दिवस" },
  { value: "Night", label: "रात्र" },
  { value: "Unusual", label: "असामान्य" },
];

const RANGE_OPTIONS = [
  { key: "Daily", type: "daily" },
  { key: "Month", type: "month" },
  { key: "6 Month", type: "6month" },
  { key: "1 Year", type: "year" },
];

const PATTERN_TAG_COLOR = {
  "Night Alert": "red",
  Regular: "green",
  "Peak Slot": "cyan",
  "Repeat Hit": "blue",
  "Burst Event": "lime",
  "Duplicate Hit": "geekblue",
};

const EVENT_CLASSIFICATION_LABEL = {
  "Night Alert": "Unusual Night Detection",
  Regular: "Regular Working-Hour Pattern",
  "Peak Slot": "Peak Slot Detection",
  "Repeat Hit": "Repeat Hit Pattern",
  "Burst Event": "Burst / Cluster Detection",
  "Duplicate Hit": "Weekend / Duplicate Hit",
};

const detailField = (label, value) => (
  <div
    style={{
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      padding: "10px 12px",
      background: "#fff",
      minHeight: 58,
    }}
  >
    <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
      {label}
    </div>
    <div
      style={{
        fontSize: 14,
        fontWeight: 700,
        color: "#0f172a",
        wordBreak: "break-word",
      }}
    >
      {value || "-"}
    </div>
  </div>
);

const EMPTY_SUMMARY = {
  rawCount: 0,
  uniqueCount: 0,
  activeDays: 0,
  peakSlot: "-",
  peakLabel: "-",
  dayPercent: 0,
  unusualAlertTime: null,
  hasPattern: false,
};

const EMPTY_HEATMAP = {
  days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  slots: ["04-08", "08-12", "12-16", "16-20", "20-24", "00-04"],
  values: [
    [0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0],
  ],
};

const getRangeDates = (type) => {
  const endDate = moment();
  let startDate = moment();
  if (type === "month") startDate = moment().startOf("month");
  else if (type === "6month") startDate = moment().subtract(6, "months");
  else if (type === "year") startDate = moment().subtract(1, "year");
  return {
    startDate: startDate.format("YYYY-MM-DD"),
    endDate: endDate.format("YYYY-MM-DD"),
  };
};

const heatColor = (v) => {
  const colors = [
    "#eff6ff",
    "#bfdbfe",
    "#93c5fd",
    "#60a5fa",
    "#3b82f6",
    "#2563eb",
    "#1d4ed8",
  ];
  return colors[Math.min(Number(v) || 0, colors.length - 1)];
};

const kpiCard = (letter, letterBg, label, value, sub) => (
  <div
    style={{
      flex: "1 1 140px",
      background: "#fff",
      border: "1px solid #e8eef7",
      borderRadius: 10,
      padding: "12px 14px",
      display: "flex",
      gap: 12,
      alignItems: "center",
      minWidth: 140,
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: letterBg,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 14,
        flexShrink: 0,
      }}
    >
      {letter}
    </div>
    <div>
      <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.2 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: "#0f172a",
          lineHeight: 1.3,
        }}
      >
        {value}
      </div>
      {sub ? <div style={{ fontSize: 10, color: "#94a3b8" }}>{sub}</div> : null}
    </div>
  </div>
);

const sectionCard = (title, children, style = {}) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e8eef7",
      borderRadius: 12,
      padding: 16,
      ...style,
    }}
  >
    <div
      style={{
        fontSize: 14,
        fontWeight: 700,
        color: "#0f172a",
        marginBottom: 12,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

/**
 * CCTV Movement Pattern Overview — loaded from CctvMasterDetails / cctv_api_logs.
 */
const CctvPatternOverviewStatic = ({
  open,
  onClose,
  masterId,
  accusedName,
  policeStation,
  startDate: parentStartDate,
  endDate: parentEndDate,
}) => {
  const [loading, setLoading] = useState(false);
  const [activeRange, setActiveRange] = useState("6 Month");
  const [startDate, setStartDate] = useState(
    parentStartDate ? moment(parentStartDate) : moment().subtract(6, "months"),
  );
  const [endDate, setEndDate] = useState(
    parentEndDate ? moment(parentEndDate) : moment(),
  );
  const [cctvLocation, setCctvLocation] = useState("all");
  const [locations, setLocations] = useState([]);
  const [detailSearch, setDetailSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [detailPage, setDetailPage] = useState(1);
  const [detectionDetailOpen, setDetectionDetailOpen] = useState(false);
  const [selectedDetection, setSelectedDetection] = useState(null);

  const [accusedInfo, setAccusedInfo] = useState(null);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [timeSlotData, setTimeSlotData] = useState([]);
  const [dayNightData, setDayNightData] = useState([]);
  const [heatmap, setHeatmap] = useState(EMPTY_HEATMAP);
  const [patternClassification, setPatternClassification] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [intelligenceOverview, setIntelligenceOverview] = useState("");
  const [detections, setDetections] = useState([]);

  const displayName = accusedInfo?.accusedName || accusedName || "-";
  const displayMasterId = masterId || accusedInfo?.masterId || "-";
  const displayStation = accusedInfo?.policeStation || policeStation || "-";

  const fetchDetails = useCallback(() => {
    if (!open || !masterId || !startDate || !endDate) return;

    setLoading(true);
    adminService
      .CctvMasterDetails({
        masterId: String(masterId),
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        callerIp: cctvLocation === "all" ? undefined : cctvLocation,
      })
      .then((res) => {
        setAccusedInfo(res?.accusedInfo || null);
        setSummary(res?.summary || EMPTY_SUMMARY);
        setTimeSlotData(res?.timeSlotData || []);
        setDayNightData(res?.dayNightData || []);
        setHeatmap(res?.heatmap || EMPTY_HEATMAP);
        setPatternClassification(res?.patternClassification || []);
        setTimeline(res?.timeline || []);
        setIntelligenceOverview(res?.intelligenceOverview || "");
        setDetections(res?.detections || []);
        setLocations(res?.locations || []);
        setDetailPage(1);
        setLoading(false);
      })
      .catch(() => {
        message.error("CCTV pattern data लोड करताना त्रुटी");
        setSummary(EMPTY_SUMMARY);
        setTimeSlotData([]);
        setDayNightData([]);
        setHeatmap(EMPTY_HEATMAP);
        setPatternClassification([]);
        setTimeline([]);
        setIntelligenceOverview("");
        setDetections([]);
        setLocations([]);
        setLoading(false);
      });
  }, [open, masterId, startDate, endDate, cctvLocation]);

  useEffect(() => {
    if (!open) return;
    if (parentStartDate) setStartDate(moment(parentStartDate));
    if (parentEndDate) setEndDate(moment(parentEndDate));
    setCctvLocation("all");
    setDetailSearch("");
    setCategoryFilter("all");
    setDetailPage(1);
    setDetectionDetailOpen(false);
    setSelectedDetection(null);
  }, [open, masterId, parentStartDate, parentEndDate]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const applyQuickRange = (label, type) => {
    const range = getRangeDates(type);
    setActiveRange(label);
    setStartDate(moment(range.startDate));
    setEndDate(moment(range.endDate));
  };

  const filteredDetectionRows = useMemo(() => {
    const q = detailSearch.trim().toLowerCase();
    return detections.filter((row) => {
      const matchCategory =
        categoryFilter === "all" || row.category === categoryFilter;
      const matchSearch =
        !q ||
        String(row.date || "")
          .toLowerCase()
          .includes(q) ||
        String(row.time || "")
          .toLowerCase()
          .includes(q) ||
        String(row.day || "")
          .toLowerCase()
          .includes(q) ||
        String(row.callerIp || "")
          .toLowerCase()
          .includes(q) ||
        String(row.pattern || "")
          .toLowerCase()
          .includes(q) ||
        String(row.resourcePath || "")
          .toLowerCase()
          .includes(q) ||
        String(row.category || "")
          .toLowerCase()
          .includes(q);
      return matchCategory && matchSearch;
    });
  }, [detections, detailSearch, categoryFilter]);

  const detectionColumns = [
    {
      title: "SR. NO",
      dataIndex: "srno",
      key: "srno",
      width: 70,
      align: "center",
    },
    { title: "DATE", dataIndex: "date", key: "date", width: 110 },
    { title: "DAY", dataIndex: "day", key: "day", width: 100 },
    { title: "TIME", dataIndex: "time", key: "time", width: 100 },
    {
      title: "TIME CATEGORY",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (text) => {
        const color =
          text === "Unusual" ? "red" : text === "Night" ? "purple" : "blue";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    { title: "CALLER IP", dataIndex: "callerIp", key: "callerIp", width: 140 },
    {
      title: "RESOURCE PATH",
      dataIndex: "resourcePath",
      key: "resourcePath",
      width: 160,
    },
    {
      title: "MASTER ID",
      dataIndex: "masterId",
      key: "masterId",
      width: 100,
      render: (text) => text || displayMasterId,
    },
    {
      title: "PATTERN",
      dataIndex: "pattern",
      key: "pattern",
      width: 120,
      render: (text) => (
        <Tag color={PATTERN_TAG_COLOR[text] || "default"}>{text}</Tag>
      ),
    },
    {
      title: "कृती",
      key: "action",
      width: 90,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Button
          type="default"
          size="small"
          style={{ color: "#2563eb", borderColor: "#93c5fd" }}
          onClick={() => {
            setSelectedDetection(record);
            setDetectionDetailOpen(true);
          }}
        >
          पहा
        </Button>
      ),
    },
  ];

  const heatmapDays = heatmap?.days || EMPTY_HEATMAP.days;
  const heatmapSlots = heatmap?.slots || EMPTY_HEATMAP.slots;
  const heatmapValues = heatmap?.values || EMPTY_HEATMAP.values;

  return (
    <Modal
      title={null}
      visible={open}
      onCancel={onClose}
      footer={null}
      width="95%"
      style={{ top: 16 }}
      destroyOnClose
      zIndex={1200}
      bodyStyle={{
        padding: 0,
        maxHeight: "90vh",
        overflowY: "auto",
        background: "#f1f5f9",
      }}
    >
      <div
        style={{
          background: "#fff",
          padding: "14px 20px",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
          CCTV Movement Pattern Overview
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Tag color="blue" style={{ cursor: "pointer" }} onClick={onClose}>
            Close
          </Tag>
        </div>
      </div>

      <Spin spinning={loading}>
        <div style={{ padding: 16 }}>
          <div
            style={{
              background:
                "linear-gradient(135deg, #1d4ed8 0%, #2563eb 55%, #3b82f6 100%)",
              borderRadius: 14,
              padding: "20px 24px",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
                {displayName}
              </div>
              <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>
                CCTV detection movement pattern analysis
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  flexWrap: "wrap",
                  fontSize: 13,
                }}
              >
                <span>
                  Master ID: <strong>{displayMasterId}</strong>
                </span>
                <span>
                  Police Station: <strong>{displayStation}</strong>
                </span>
              </div>
            </div>
            <div
              style={{
                width: 110,
                height: 110,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.18)",
                border: "3px solid rgba(255,255,255,0.45)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
                {summary.rawCount}
              </div>
              <div
                style={{
                  fontSize: 10,
                  textAlign: "center",
                  opacity: 0.95,
                  marginTop: 4,
                }}
              >
                Total CCTV
                <br />
                Records
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              border: "1px solid #e8eef7",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 14,
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
              कालावधी:
            </span>
            {RANGE_OPTIONS.map((item) => (
              <span
                key={item.key}
                onClick={() => applyQuickRange(item.key, item.type)}
                style={{
                  padding: "4px 12px",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: activeRange === item.key ? "#dbeafe" : "#f8fafc",
                  color: activeRange === item.key ? "#2563eb" : "#475569",
                  border:
                    activeRange === item.key
                      ? "1px solid #93c5fd"
                      : "1px solid #e2e8f0",
                }}
              >
                {item.key}
              </span>
            ))}
            <DatePicker
              value={startDate}
              format="DD / MM / YYYY"
              allowClear={false}
              onChange={(date) => {
                if (date) {
                  setActiveRange("");
                  setStartDate(date);
                }
              }}
              style={{ minWidth: 140 }}
              getCalendarContainer={() => document.body}
              popupStyle={{ zIndex: 1400 }}
            />
            <span style={{ fontSize: 13, color: "#64748b" }}>ते</span>
            <DatePicker
              value={endDate}
              format="DD / MM / YYYY"
              allowClear={false}
              onChange={(date) => {
                if (date) {
                  setActiveRange("");
                  setEndDate(date);
                }
              }}
              style={{ minWidth: 140 }}
              getCalendarContainer={() => document.body}
              popupStyle={{ zIndex: 1400 }}
            />
            <Select
              value={cctvLocation}
              onChange={setCctvLocation}
              style={{ minWidth: 220 }}
              placeholder="Caller IP / स्थान"
              getPopupContainer={() => document.body}
              dropdownStyle={{ zIndex: 1400 }}
            >
              <Option value="all">सर्व Caller IP</Option>
              {locations.map((loc) => (
                <Option key={loc} value={loc}>
                  {loc}
                </Option>
              ))}
            </Select>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              marginBottom: 14,
            }}
          >
            {kpiCard(
              "R",
              "#3b82f6",
              "Raw",
              String(summary.rawCount),
              "records",
            )}
            {kpiCard(
              "U",
              "#22c55e",
              "Filtered",
              String(summary.uniqueCount),
              "unique detection events",
            )}
            {kpiCard(
              "D",
              "#14b8a6",
              "Days",
              String(summary.activeDays),
              "active days",
            )}
            {kpiCard(
              "P",
              "#f59e0b",
              "Peak",
              summary.peakSlot || "-",
              "time range",
            )}
            {kpiCard(
              "%",
              "#8b5cf6",
              "Day",
              `${summary.dayPercent}%`,
              "daytime activity",
            )}
            {kpiCard(
              "!",
              "#ef4444",
              "Alert",
              summary.unusualAlertTime || "-",
              "unusual time",
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1fr",
              gap: 12,
              marginBottom: 14,
            }}
          >
            {sectionCard(
              "CCTV Detection — Time-slot wise distribution",
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={timeSlotData}
                    margin={{ top: 8, right: 8, left: -10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="slot" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>,
            )}
            {sectionCard(
              "Day / Night Movement",
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{ width: "55%", height: 220, position: "relative" }}
                >
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={dayNightData}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                      >
                        {dayNightData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 800,
                        color: "#0f172a",
                      }}
                    >
                      {summary.uniqueCount}
                    </div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>
                      Unique Events
                    </div>
                  </div>
                </div>
                <div style={{ flex: 1, fontSize: 12 }}>
                  {dayNightData.map((d) => (
                    <div
                      key={d.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: d.color,
                          display: "inline-block",
                        }}
                      />
                      <span style={{ color: "#475569", flex: 1 }}>
                        {d.name}
                      </span>
                      <strong>{d.value}%</strong>
                    </div>
                  ))}
                </div>
              </div>,
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.2fr 0.9fr 0.9fr",
              gap: 12,
              marginBottom: 14,
            }}
          >
            {sectionCard(
              "Heatmap — Day & Time Pattern",
              <div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `56px repeat(${heatmapSlots.length}, 1fr)`,
                    gap: 4,
                    marginBottom: 4,
                  }}
                >
                  <div />
                  {heatmapSlots.map((s) => (
                    <div
                      key={s}
                      style={{
                        fontSize: 10,
                        textAlign: "center",
                        color: "#64748b",
                        fontWeight: 600,
                      }}
                    >
                      {s}
                    </div>
                  ))}
                </div>
                {heatmapDays.map((day, di) => (
                  <div
                    key={day}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `56px repeat(${heatmapSlots.length}, 1fr)`,
                      gap: 4,
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "#475569",
                        fontWeight: 600,
                      }}
                    >
                      {day}
                    </div>
                    {(heatmapValues[di] || []).map((v, si) => (
                      <div
                        key={`${day}-${si}`}
                        title={`${day} ${heatmapSlots[si]}: ${v}`}
                        style={{
                          height: 28,
                          borderRadius: 4,
                          background: heatColor(v),
                          border: "1px solid #e2e8f0",
                        }}
                      />
                    ))}
                  </div>
                ))}
              </div>,
            )}

            {sectionCard(
              "Pattern Classification",
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {(patternClassification.length
                  ? patternClassification
                  : [
                      {
                        letter: "R",
                        bg: "#3b82f6",
                        title: "Regular Working-Hour Pattern",
                      },
                      {
                        letter: "W",
                        bg: "#f59e0b",
                        title: "Multiple Weekday Presence",
                      },
                      {
                        letter: "!",
                        bg: "#ef4444",
                        title: "Unusual Night Detection",
                      },
                      {
                        letter: "B",
                        bg: "#22c55e",
                        title: "Burst / Repeat Hit Pattern",
                      },
                    ]
                ).map((p) => (
                  <div
                    key={p.title}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 10px",
                      background: p.active === false ? "#f8fafc" : "#f8fafc",
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                      opacity: p.active === false ? 0.45 : 1,
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: p.bg,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {p.letter}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#334155",
                      }}
                    >
                      {p.title}
                    </span>
                  </div>
                ))}
              </div>,
            )}

            {sectionCard(
              "Recent Movement Timeline",
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {timeline.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>
                    No recent detections
                  </div>
                ) : (
                  timeline.map((item) => (
                    <div
                      key={`${item.time}-${item.text}`}
                      style={{ display: "flex", gap: 10 }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: item.color,
                          marginTop: 5,
                          flexShrink: 0,
                        }}
                      />
                      <div>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>
                          {item.time}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#334155",
                            fontWeight: 500,
                          }}
                        >
                          {item.text}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>,
            )}
          </div>

          <div
            style={{
              background: "#fff",
              border: "1px solid #e8eef7",
              borderRadius: 12,
              padding: 16,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                CMIS CCTV Intelligence Overview
              </div>
              <Tag color={summary.hasPattern ? "success" : "default"}>
                {summary.hasPattern ? "Pattern Found" : "No Data"}
              </Tag>
            </div>
            <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
              {intelligenceOverview ||
                "No analysis available for the selected filters."}
            </div>
          </div>

          {summary.unusualAlertTime ? (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 12,
                padding: "12px 16px",
                marginBottom: 14,
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "#ef4444",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                !
              </span>
              <div>
                <div
                  style={{ fontWeight: 700, color: "#b91c1c", marginBottom: 4 }}
                >
                  Unusual Movement Alert
                </div>
                <div style={{ fontSize: 13, color: "#7f1d1d" }}>
                  Night detection recorded at{" "}
                  <strong>{summary.unusualAlertTime}</strong> — outside normal
                  activity window for Master ID {displayMasterId}.
                </div>
              </div>
            </div>
          ) : null}

          <div
            style={{
              background: "#fff",
              border: "1px solid #e8eef7",
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 14,
              }}
            >
              <div>
                <div
                  style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}
                >
                  Detection Details
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  Date, Time, Caller IP, Event Type आणि Pattern Classification
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Input
                  allowClear
                  prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                  placeholder="तारीख / वेळ शोधा..."
                  value={detailSearch}
                  onChange={(e) => {
                    setDetailSearch(e.target.value);
                    setDetailPage(1);
                  }}
                  style={{ width: 200 }}
                />
                <Select
                  value={categoryFilter}
                  onChange={(value) => {
                    setCategoryFilter(value);
                    setDetailPage(1);
                  }}
                  style={{ minWidth: 130 }}
                  getPopupContainer={() => document.body}
                  dropdownStyle={{ zIndex: 1400 }}
                >
                  {CATEGORY_FILTER_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>

            <Table
              size="small"
              bordered
              dataSource={filteredDetectionRows}
              columns={detectionColumns}
              rowKey={(row) => row.id || row.srno}
              scroll={{
                x: 1200,
                y: "calc(100vh - 350px)",
              }}
              pagination={{
                current: detailPage,
                pageSize: 10,
                total: filteredDetectionRows.length,
                showSizeChanger: false,
                onChange: (page) => setDetailPage(page),
                showTotal: (total) =>
                  `दाखविलेल्या नोंदी: ${Math.min(10, total)} / ${summary.rawCount}`,
              }}
            />
          </div>
        </div>
      </Spin>

      <Modal
        title={null}
        visible={detectionDetailOpen}
        onCancel={() => {
          setDetectionDetailOpen(false);
          setSelectedDetection(null);
        }}
        footer={null}
        width={560}
        centered
        destroyOnClose
        zIndex={1300}
        closable={false}
        bodyStyle={{ padding: 0 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 18px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            CCTV Detection Detail
          </div>
          <Button
            type="text"
            icon={<CloseOutlined style={{ color: "#ef4444" }} />}
            onClick={() => {
              setDetectionDetailOpen(false);
              setSelectedDetection(null);
            }}
            style={{
              background: "#fee2e2",
              borderRadius: 8,
              width: 32,
              height: 32,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        </div>

        <div style={{ padding: 16 }}>
          <div
            style={{
              background: "linear-gradient(90deg, #2563eb 0%, #22d3ee 100%)",
              borderRadius: 12,
              minHeight: 150,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              marginBottom: 14,
              textAlign: "center",
              padding: 16,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
              CCTV Snapshot Preview
            </div>
            <div style={{ fontSize: 12, opacity: 0.95 }}>
              Actual snapshot / camera image येथे दाखवता येईल
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            {detailField(
              "Master ID",
              selectedDetection?.masterId || displayMasterId,
            )}
            {detailField("पोलीस स्टेशन", displayStation)}
            {detailField("तारीख", selectedDetection?.date)}
            {detailField("वेळ", selectedDetection?.time)}
            {detailField(
              "Event Classification",
              EVENT_CLASSIFICATION_LABEL[selectedDetection?.pattern] ||
                selectedDetection?.pattern,
            )}
            {detailField("Caller IP", selectedDetection?.callerIp)}
            {detailField("Camera Name", "Camera Mapping Required")}
            {detailField("Location", "Location Mapping Required")}
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default CctvPatternOverviewStatic;
